import {Component,OnDestroy,OnInit} from "@angular/core";
import {ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {ListItem} from "@domain/common/list-view/list-item";
import {TranslateService} from "@ngx-translate/core";
import {Sorting} from "@domain/common/list-view/sorting";
import {IndemnitesReglesAttributionListItemComponent} from "@components/admin/frais/indemnites/indemnites-regles-attribution/indemnites-regles-attribution-list-item/indemnites-regles-attribution-list-item.component";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {BehaviorSubject} from "rxjs";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {ActivatedRoute,Router} from "@angular/router";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {filter} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {environment} from "@environments/environment";
import {ToastrService} from "ngx-toastr";

/**
 * Onglet règles d'attribution
 *
 * @author Guillaume TRAVOT
 * @date 26/04/2024
 */
@Component({
    host: {'[attr.data-test-id]': "'indemnites-regles-attribution'"},
    templateUrl: './indemnites-regles-attribution.component.html'
})
export class IndemnitesReglesAttributionComponent implements OnInit, OnDestroy {
    /** Liste des règles d'attribution en affichage V10 */
    listeReglesAttribution: ListView<IndemnitesRegleAttributionItem,IndemnitesReglesAttributionListItemComponent>;

    /** Identifiant de la règle à afficher en legacy (détail / création) */
    idRegleAttribution: string;

    /** Liste des actions du bouton flottant */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Identifiant du message de retour liste depuis la frame legacy */
    private static readonly MESSAGE_RETOUR_LISTE = 'reglesAttributionRetourListe';

    /** Handler d'écoute des messages legacy */
    private readonly legacyMessageHandler = (event: MessageEvent) => {
        //Prise en charge uniquement du message dédié à cet écran
        if (event?.data === IndemnitesReglesAttributionComponent.MESSAGE_RETOUR_LISTE) {
            void this.router.navigate(['Admin/Frais/Indemnites/ReglesAttribution']);
        }
    };

    /**
     * Constructeur
     */
    constructor(private translateService: TranslateService,
                private router: Router,
                private activatedRoute: ActivatedRoute,
                private confirmService: ConfirmService,
                private http: HttpClient,
                private toastrService: ToastrService) {
    }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Écoute du retour liste demandé depuis la frame legacy
        window.addEventListener('message', this.legacyMessageHandler);

        //Pilotage de l'affichage via query param pour conserver la barre d'onglets
        this.activatedRoute.queryParamMap.subscribe((queryParamMap) => {
            //Détection de l'affichage détail/création embarqué
            this.idRegleAttribution = queryParamMap.get('idRegleAttribution');

            //Initialisation de la liste uniquement en mode liste
            if (!this.idRegleAttribution && !this.listeReglesAttribution) {
                this.initListView();
            }
        });
    }

    /**
     * Nettoyage du composant
     */
    ngOnDestroy(): void {
        //Nettoyage de l'écoute des messages legacy
        window.removeEventListener('message', this.legacyMessageHandler);
    }

    /**
     * Initialisation de la liste V10
     */
    private initListView(): void {
        //Initialisation du bouton flottant de création
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-ajouter',
            libelle: 'global.actions.creer',
            doAction: () => this.createRegleAttribution()
        }]);

        //Initialisation de la liste V10 en mode backend standard
        this.listeReglesAttribution = new ListView<IndemnitesRegleAttributionItem,IndemnitesReglesAttributionListItemComponent>({
            uri: '/controller/RegleAttribution/listeReglesAttributionSearch',
            title: this.translateService.instant('admin.frais.indemnites.onglets.reglesAttribution'),
            component: IndemnitesReglesAttributionListItemComponent,
            defaultOrder: 'libelle',
            isFilter: true,
            isSearchBar: true,
            isFilterVisible: true,
            mapResult: (result: Result) => this.mapSearchResultToPage(result),
            listeFilters: [{
                clef: 'libelle',
                title: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.filtres.libelle'),
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'modeGestion',
                title: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.filtres.modeGestion'),
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                multiple: true,
                listeValues: [{
                    code: 'UNE_FOIS_PAR_JOUR',
                    libelle: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.modesGestion.uneFoisParJour')
                }, {
                    code: 'TRANCHE_HORAIRE',
                    libelle: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.modesGestion.trancheHoraire')
                }, {
                    code: 'TEMPS_PASSE',
                    libelle: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.modesGestion.tempsPasse')
                }]
            }, {
                clef: 'isActif',
                title: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.filtres.actif'),
                type: TypeFilter[TypeFilter.BOOLEAN],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                isDefault: false,
                onloadValue: 'true'
            }],
            extraOptions: {
                deleteRegleAttribution: (regleAttribution: IndemnitesRegleAttributionItem) => this.deleteRegleAttribution(regleAttribution),
                consultRegleAttribution: (regleAttribution: IndemnitesRegleAttributionItem) => this.consultRegleAttribution(regleAttribution)
            }
        });

        //Nom de classe pour la persistance des filtres
        this.listeReglesAttribution.className = 'IndemnitesReglesAttributionV10Component';

        //Activation de la persistance des filtres
        this.listeReglesAttribution.isPersistFilters = true;

        //Colonnes triables
        this.listeReglesAttribution.columns = [
            {key: 'libelle',title: this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.colonnes.libelle')}
        ];

        //Tri par défaut
        this.listeReglesAttribution.sorting = new Sorting(this.listeReglesAttribution.columns,'libelle');
    }

    /**
     * Mapping de la liste des règles d'attribution depuis la réponse backend
     */
    private mapSearchResultToPage(result: Result): any {
        const sourceListe = result?.data?.listeReglesAttribution ?? result;
        const donneesBrutes = sourceListe?.listeResultats ?? sourceListe?.data ?? [];
        const listeResultats = (donneesBrutes ?? []).map((item: Partial<IndemnitesRegleAttributionItem>) => new IndemnitesRegleAttributionItem(item));

        return {
            listeResultats,
            numPage: sourceListe?.nbPagesTotal !== undefined ? (sourceListe?.numPage ?? 0) : Math.max((sourceListe?.numPage ?? 1) - 1,0),
            nbPagesTotal: sourceListe?.nbPagesTotal ?? sourceListe?.nbPages ?? 1,
            nbObjetsTotal: sourceListe?.nbObjetsTotal ?? sourceListe?.nbObjets ?? listeResultats.length,
            nbObjetsParPage: sourceListe?.nbObjetsParPage ?? sourceListe?.objetParPage ?? 25,
            nbObjetsDansPage: listeResultats.length
        };
    }

    /**
     * Suppression d'une règle d'attribution
     */
    private deleteRegleAttribution(regleAttribution: IndemnitesRegleAttributionItem): void {
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter((isConfirmed: boolean) => isConfirmed))
            .subscribe(() => {
                this.http.delete<Result>(`${environment.baseUrl}/controller/RegleAttribution/deleteRegleAttribution/${regleAttribution.id}`).subscribe(result => {
                    if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                        this.toastrService.success(this.translateService.instant('global.success.suppression'));
                        this.listeReglesAttribution?.refresh();
                    } else {
                        this.toastrService.error(this.translateService.instant('admin.frais.indemnites.reglesAttribution.liste.erreurs.suppressionImpossible'));
                    }
                });
            });
    }

    /**
     * Redirection vers l'écran de création d'une règle d'attribution (comportement V9)
     */
    private createRegleAttribution(): void {
        void this.router.navigate(['Admin/Frais/Indemnites/ReglesAttribution/0']);
    }

    /**
     * Ouverture d'une règle d'attribution existante
     */
    private consultRegleAttribution(regleAttribution: IndemnitesRegleAttributionItem): void {
        void this.router.navigate([`Admin/Frais/Indemnites/ReglesAttribution/${regleAttribution.id}`]);
    }
}

/**
 * Modèle d'item de la liste des règles d'attribution
 */
export class IndemnitesRegleAttributionItem implements ListItem {
    /** Identifiant de la règle */
    id: number;

    /** Libellé de la règle */
    libelle: string;

    /** Mode de gestion de la règle */
    modeGestion: string;

    /** Indique si la règle est active */
    actif: boolean;

    /** Nombre de prestations liées */
    nombrePrestations: number;

    /** Liste des libellés de prestations liées (tooltip) */
    libellesPrestations: string;

    /** Nombre de domaines de prestations liés */
    nombreDomainesPrest: number;

    /** Libellé du domaine de prestation lié */
    libelleDomainePrestation: string;

    /** Heure de début pour le mode tranche horaire */
    heureDeb: string;

    /** Heure de fin pour le mode tranche horaire */
    heureFin: string;

    /** Durée minimale pour le mode temps passé */
    dureeMin: number;

    /** Durée maximale pour le mode temps passé */
    dureeMax: number;

    /**
     * Constructeur
     */
    constructor(data?: Partial<IndemnitesRegleAttributionItem>) {
        Object.assign(this,data);
    }

    /**
     * Retourne la clé de l'élément
     */
    getKey(): number {
        return this.id;
    }
}
